<template>
	<Modal
		:title="title"
		@close="close"
	>
		<!-- BODY -->
		<template #body>
			<div class="modal-body">
				<!-- PREVIEW -->
				<div
					class="preview-w"
					role="region"
				>
					<!-- IMAGE -->
					<div
						v-if="event"
						class="h-16-9 no-image-placeholder"
						:style="`background-image: url('${$Helpers.getScaledImageUrl(event.imageUrl, 360)}')`"
					/>

					<!-- DURATION BADGE -->
					<div class="card-grid-item__badge -right">
						<div class="badge--status">
							<div class="badge--status__text">
								<Icon
									class="icon-play"
									icon-name="button-play-filled"
									size="xs"
								/>
								{{ durationString }}
							</div>
						</div>
					</div>
				</div>

				<!-- LOADING SPINNER -->
				<LoadingSpinner
					v-if="!videoParts"
					center
				/>

				<!-- NO VIDEO PARTS DOWNLOAD WARNING -->
				<div
					v-show="videoParts && videoParts.length === 0"
				>
					<Icon
						class="icon-warning"
						icon-name="c-warning"
					/> {{ $I18n.trans('event.messages.no_download_yet') }}
				</div>

				<!-- HAS VIDEOPARTS -->
				<div v-show="videoParts && videoParts.length > 0">
					<!-- QUALITY DROPDOWN -->
					<FormField
						class="mt-2"
						:label="$I18n.trans('commons.quality')"
					>
						<Dropdown
							v-model="selectedVideoPart"
							:options="dropdownVideoParts"
							is-small
							@change="onHeightChange"
						/>
					</FormField>

					<!-- VIDEO PART SIZE LABEL -->
					<div
						v-if="selectedVideoPart && !startSeconds"
						class="h-right text-sm mt-2"
					>
						{{ selectedVideoPart.size | formatSize }}
					</div>

					<!-- OVERLAY CHECKBOX -->
					<div
						v-if="meta && meta.overlayUrls && meta.overlayUrls.length > 0"
						class="mt-2"
					>
						<Checkbox
							id="overlay"
							v-model="overlay"
							size="small"
							:label="$I18n.trans('event.download.attributes.overlay')"
						/>
					</div>

					<!-- DESCRIPTION -->
					<FormField
						:label="$I18n.trans('commons.description')"
						class="mt-2"
					>
						<FormInput
							id="`mdv_description`"
							v-model="description"
							:is-valid="description.length <= 100"
						/>
					</FormField>

					<!-- COPY URL -->
					<FormField
						v-if="meta && meta.canShareLink"
						class="mt-2"
						:label="$I18n.trans('commons.copy_link')"
					>
						<FormInput
							:value="urlBoxText"
							:readonly="true"
							:classes="{'url-copied': urlIsCopied }"
						>
							<template
								v-if="!urlIsCopied"
								#trailing
							>
								<a
									class="url-icon"
									@click="copyToClipboard"
								>
									<Icon
										icon-name="copy"
										color-class="sc-gray-light"
									/>
								</a>
							</template>
						</FormInput>
					</FormField>
				</div>
			</div>
		</template>

		<template #footer-buttons>
			<!-- DOWNLOAD BUTTON -->
			<button
				class="download-btn button button--secondary"
				:disabled="!event || !selectedVideoPart"
				@click="handleConfirm(selectedVideoPart.id)"
			>
				<Icon
					class="icon-social"
					icon-name="cloud-download"
				/>
				{{ $Helpers.ucfirst($I18n.trans('commons.download')) }}
			</button>
		</template>
	</Modal>
</template>

<script>
import copy from 'copy-to-clipboard';
import laroute from '@laroute';
import Dropdown from '../dropdown';
import FormInput from '../../analysis/components/Form/FormInput';
import FormField from '../../analysis/components/Form/FormField';
import Checkbox from '../checkbox/Checkbox';
import { LoadingSpinner } from '../loader';
import Modal from '../modal';

const FORBIDDEN_VIDEO_PART_HEIGHT = 480;
const PART_STATUS = {
	AVAILABLE: 'available',
};

export default {
	name:       'DownloadVideoModal',
	components: { Dropdown, FormField, FormInput, Checkbox, Modal, LoadingSpinner },
	props:      {
		title: {
			type:    String,
			default: '',
		},
		startSeconds: {
			type:    Number,
			default: null,
		},
		endSeconds: {
			type:    Number,
			default: null,
		},
		event: {
			type:     Object,
			default:  null,
			required: true,
		},
		tagName: {
			type:    String,
			default: '',
		},
	},
	data() {
		return {
			description:        '',
			overlay:            false,
			videoParts:         null,
			dropdownVideoParts: [],
			selectedVideoPart:  null,
			shareLink:          null,
			meta:               null,
			urlIsCopied:        false,
		};
	},
	computed: {
		durationString() {
			if (this.event) {
				let { durationSeconds } = this.event;
				if (this.startSeconds && this.endSeconds) {
					durationSeconds = this.endSeconds - this.startSeconds;
				}

				return moment
					.duration({ seconds: durationSeconds })
					.format(`${durationSeconds >= 3600 ? 'H:' : ''}mm:ss`, {
						trim: false,
					});
			}
			return '';
		},
		selectedVideoPartId() {
			return this?.selectedVideoPart?.id;
		},
		confirmationText() {
			return this.transUcfirst('commons.copied_message');
		},
		urlBoxText() {
			return this.urlIsCopied ? this.confirmationText : this.shareLink;
		},
	},
	watch: {
		overlay() {
			this.updateShareLink();
		},
		selectedVideoPart() {
			this.updateShareLink();
		},
	},
	mounted() {
		this.description = this.event.name;
		this.getVideoParts();
	},
	methods: {
		copyToClipboard() {
			copy(this.shareLink);
			this.urlIsCopied = true;
		},
		onHeightChange(videoPartEvent) {
			if (this.videoParts) {
				this.selectedVideoPart = this.videoParts.find((videoPart) => videoPart.id === videoPartEvent.id);
			}
		},
		async updateShareLink() {
			if (this?.meta?.canShareLink) {
				this.shareLink = null;
				const filename = this.getFilename();
				const { data } = await axios
					.get(laroute.route('api.event.video_part.download', {
						event:       this.event.id,
						videoPartId: this.selectedVideoPart.id,
					}) + this.getQueryString({
						shortlink: true,
						filename:  filename ? encodeURIComponent(filename) : null,
					}))
					.catch(err => {
						console.error(err);
					});

				if (data) {
					this.shareLink = data;
				}
			}
		},
		getFilename() {
			return this.description.length > 0 ? [
				moment(this.start).format('YYYY_MM_DD'),
				this.$Helpers.slugify(this.description),
				this.tagName ? this.$Helpers.slugify(this.tagName) : null,
				this.selectedVideoPart.isSource ? 'original' : `${this.selectedVideoPart.height}p`,
			].filter(value => !!value).join('_') : null;
		},
		getQueryString(additionalParams) {
			const params = additionalParams || {};

			if (this.startSeconds && this.endSeconds) {
				params.startSeconds = this.startSeconds;
				params.endSeconds = this.endSeconds;
			}

			if (this.meta.canShareLink) {
				params.expiresInSeconds = 60 * 60 * 24 * 14;
				if (this.overlay) {
					params.overlayUrl = this.meta.overlayUrls[0];
				}
			}

			return Object.keys(params).length > 0 ? `?${new URLSearchParams(params).toString()}` : '';
		},
		async handleConfirm(videoPartId) {
			const { data } = await axios
				.get(laroute.route('api.event.video_part.download', {
					event: this.event.id,
					videoPartId,
				}) + this.getQueryString())
				.catch((err) => {
					console.error(err);
				});

			if (data) {
				const filename = this.getFilename();
				const link = document.createElement('a');
				link.href = data + (filename ? `&filename=${encodeURIComponent(filename)}` : '');
				link.click();
			}

			this.close();
		},
		async getVideoParts() {
			const {
				data: { data: responseParts, meta },
			} = await axios.get(laroute.route('api.event.video_parts', { event: this.event.id }));
			this.meta = meta;
			const containsSource = responseParts.some(part => part.isSource);

			let parts = [...responseParts];
			parts.sort((a, b) => {
				if (a.isSource || b.isSource) return a.isSource ? 1 : -1;
				return a.height - b.height;
			});
			parts = parts
				.filter(
					part =>
						(part.height !== FORBIDDEN_VIDEO_PART_HEIGHT || part.isSource) &&
						part.status === PART_STATUS.AVAILABLE
				);
			parts = parts.slice(containsSource ? -4 : -3); // limit to the three largest resolutions to minimize dropdown size
			parts =	parts.map((part, index) => ({
				...part,
				quality: part.isSource ? 'original' : ['low', 'medium', 'high'][index],
			}));
			parts.reverse();

			this.videoParts = parts;
			this.selectedVideoPart = parts[containsSource && parts.length > 1 ? 1 : 0]; // preselect largest video height, but not the source
			this.dropdownVideoParts = this.videoParts.reduce((result, videoPart) => {
				const { id, height, quality } = videoPart;
				const translation = this.$Helpers.ucfirst(this.$I18n.trans(`commons.${quality}`));

				result.push({
					key:   id,
					id,
					label: `${translation}${(height && ` ${height}p`) || ''}`,
					value: videoPart,
				});
				return result;
			}, []);
		},
		close() {
			this.$emit('close');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.download-btn {
	display: flex;
	align-items: center;
	justify-content: center;

	div {
		margin-right: 10px;
	}
}

.modal-body {
	display: flex;
	flex-direction: column;

	.preview-w {
		position: relative;
		margin-bottom: 0.75rem;
	}

	.label {
		@include text-md-1;
		margin-bottom: 6px;
		color: var(--color-soft-white);
	}
}

.no-image-placeholder {
	background-color: gray;
}

.icon-warning {
	color: var(--color-red);
}

.url-copied::v-deep {
	color: var(--color-success);
	border: 1px solid var(--color-hype-yellow);
}

.card-grid-item__badge {
	background: rgba(var(--color-rgb-black), 0.5);
	position: absolute;
	bottom: 24px;

	&.-right {
		right: 24px;

		.badge--status {
			margin: 0;
		}
	}

	.badge--status__text {
		display: flex;
		align-items: center;
		@include text-sm-2;
		height: 24px;
		padding: 3px 8px;
		vertical-align: center;
		margin: 0;

		div {
			margin-right: 8px;
		}
	}
}
</style>
